import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { JobFamilyCreateModelInput, JobFamilyGroupModel, JobFamilyPatchModelInput } from '@utmost.co/enterprise-models';
import { Dialog, Input, Select, Button } from '@beeline.com/bp-design-system';
import { useSnackbar } from 'src/utils/hooks/useSnackbar';
import { UseState } from 'src/types';
import { useJobFamilies } from './useJobFamilies';
import { JobFamily } from './jobFamily.graphql';
import { useGetAllJobFamilyData } from './useGetAllJobFamilyData';
import { useDisabledJobFamilyFields } from './useDisabledJobFamilyFields';
import { useJobFamiliesIntl } from './translations/useJobFamiliesIntl';

const prepareJobFamilyCreateInput = (jobFamily: Partial<JobFamily>): DeepPartial<JobFamilyCreateModelInput> => ({
  status: 'ACTIVE',
  name: jobFamily?.name ?? '',
  referenceId: jobFamily?.referenceId ?? '',
  jobFamilyGroupReference: jobFamily?.jobFamilyGroup ? { uid: jobFamily.jobFamilyGroup.uid } : undefined,
  summary: jobFamily?.summary ?? ''
});

const prepareJobFamilyPatchInput = (jobFamily: Partial<JobFamily>): DeepPartial<JobFamilyPatchModelInput> => ({
  name: jobFamily?.name ?? '',
  referenceId: jobFamily?.referenceId ?? '',
  jobFamilyGroupReference: jobFamily?.jobFamilyGroup ? jobFamily.jobFamilyGroup.uid : '',
  summary: jobFamily?.summary ?? ''
});

interface Props {
  addModalOpen: boolean;
  setAddModal: UseState<boolean>;
  editableResource?: JobFamily | null;
  onComplete: () => void;
}

const JobFamilyModalComponent: FC<React.PropsWithChildren<Props>> = ({
  addModalOpen,
  onComplete,
  setAddModal,
  editableResource
}) => {
  const intl = useJobFamiliesIntl();
  const { showError } = useSnackbar();
  const [jobFamily, setJobFamily] = useState<Partial<JobFamily>>({ ...editableResource });
  const { create, patch, loading: loadingJobFamily } = useJobFamilies();
  const { fullData, gettingFullData } = useGetAllJobFamilyData();
  const loading = gettingFullData || loadingJobFamily;
  const jobFamilyGroups = fullData?.jobFamilyGroups?.items || [];

  const handleClose = useCallback(
    (callComplete: boolean) => {
      if (callComplete) onComplete();
      setAddModal(false);
      setJobFamily({});
    },
    [setAddModal, onComplete]
  );

  const handleCreateJobFamily = useCallback(async () => {
    try {
      await create({
        mutation: prepareJobFamilyCreateInput(jobFamily)
      });
      handleClose(true);
    } catch {}
  }, [handleClose, create, jobFamily]);

  const handelPatchJobFamily = useCallback(async () => {
    try {
      await patch({
        jobFamilyUid: jobFamily.uid!,
        jobFamily: {
          ...prepareJobFamilyPatchInput(jobFamily)
        }
      });
      handleClose(true);
    } catch (err) {
      showError((err as any).toString());
    }
  }, [handleClose, jobFamily, patch, showError]);

  const isFormValid = useMemo(() => jobFamily?.name && jobFamily?.referenceId, [jobFamily]);
  const disabledFieldsResult = useDisabledJobFamilyFields({ loading, editableResource });

  return (
    <Dialog
      title={editableResource ? intl.updateJobFamily : intl.createNewJobFamily}
      subTitle={editableResource ? intl.updateJobFamilyDetails : intl.enterDetailsToCreateJobFamily}
      open={addModalOpen}
      onClose={() => handleClose(false)}
      show={{ cancelButton: true }}
      width="sm"
      actions={[
        <Button
          disabled={!isFormValid || disabledFieldsResult}
          loading={loading}
          dataTest="job-family__dialog__save"
          variant="contained"
          color="primary"
          onClick={editableResource ? handelPatchJobFamily : handleCreateJobFamily}
          key="confirm"
        >
          {intl.save}
        </Button>
      ]}
    >
      <StyledFirstLine key="name">
        <Input
          required
          label={intl.name}
          disabled={disabledFieldsResult}
          dataTest="job-family__form__name"
          fullWidth
          value={jobFamily?.name || ''}
          onChange={(name) =>
            setJobFamily({
              ...jobFamily,
              name: name.target.value
            })
          }
        />
      </StyledFirstLine>
      <StyledLine key="referenceId">
        <Input
          required
          label={intl.referenceId}
          disabled={disabledFieldsResult}
          dataTest="job-family__form__reference-id"
          fullWidth
          value={jobFamily?.referenceId || ''}
          onChange={(referenceId) =>
            setJobFamily({
              ...jobFamily,
              referenceId: referenceId.target.value
            })
          }
        />
      </StyledLine>
      <StyledLine key="jobFamilyGroup">
        <StyledMuiSelectWrapper $isDisabled={disabledFieldsResult}>
          <Select<string>
            fullWidth
            autoComplete
            label={intl.jobFamilyGroup}
            disabled={disabledFieldsResult}
            valueOptions={{
              value: jobFamily?.jobFamilyGroup?.uid ?? '',
              label: jobFamily?.jobFamilyGroup?.name ?? ''
            }}
            dataTest="job-family__form__job-family-group"
            options={jobFamilyGroups?.map((jfg) => ({ label: jfg.name, value: jfg.uid }))}
            className={jobFamilyGroups ? 'data-loaded' : undefined}
            onOptionChange={(option) => {
              // TS type guard
              if (option && !Array.isArray(option)) {
                setJobFamily({
                  ...jobFamily,
                  jobFamilyGroup: jobFamilyGroups?.find((item) => item.uid === option.value) as JobFamilyGroupModel
                });
              } else if (!option) {
                setJobFamily({
                  ...jobFamily,
                  jobFamilyGroup: null
                });
              }
            }}
          />
        </StyledMuiSelectWrapper>
      </StyledLine>
      <StyledLine key="summary">
        <Input
          label={intl.summary}
          dataTest="job-family__form__summary"
          fullWidth
          value={jobFamily?.summary || ''}
          multiline
          minRows={3}
          autoHeight
          onChange={(summary) =>
            setJobFamily({
              ...jobFamily,
              summary: summary.target.value
            })
          }
        />
      </StyledLine>
    </Dialog>
  );
};
const StyledLine = styled.div`
  margin: 1rem 0;
  min-height: 35px;
`;
const StyledFirstLine = styled(StyledLine)`
  margin-top: 0;
`;
const StyledMuiSelectWrapper = styled.div<{
  $isDisabled?: boolean;
}>`
  .MuiAutocomplete-root,
  .MuiInputBase-root {
    background-color: ${({ $isDisabled }) => ($isDisabled ? '#f0f1f2' : 'inherit')};
  }
`;
export const JobFamilyModal = memo(JobFamilyModalComponent);
